import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Grid,
    Box,
    Typography,
    CircularProgress,
    Chip,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    Circle as CircleIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    Groups as TeamIcon,
    Business as CompanyIcon,
    Public as GlobalIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useResponsive } from '../../hooks/useResponsive';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';
import {
    subscribeToPresence,
    subscribeToEvents,
    getActivityStats,
    formatRelativeTime,
    getActionDescription,
    getUserLastActivity
} from '../../services/activityService';

// Status colors for presence indicators
const STATUS_COLORS = {
    active: '#00ff41',
    online: '#5fd4c4',
    idle: '#f5a623',
    offline: '#666',
};

// Category colors for activity events
const CATEGORY_COLORS = {
    project: '#5fd4c4',
    scenario: '#9b59b6',
    optimization: '#00ff41',
    navigation: '#666',
    session: '#b28c50',
    default: '#888',
};

/**
 * StatCard component - displays a single metric with title, value, icon, color
 */
const StatCard = ({ title, value, icon: Icon, color = '#5fd4c4', isRetro, isMobile }) => (
    <Box
        sx={{
            padding: isMobile ? 1.5 : 2.5,
            background: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
            border: isRetro ? '1px solid rgba(63, 106, 100, 0.4)' : '1px solid',
            borderColor: isRetro ? undefined : 'divider',
            borderRadius: isRetro ? 0 : 2,
            height: '100%',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: isMobile ? '0.55rem' : (isRetro ? '0.65rem' : '0.75rem'),
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: isRetro ? '0.1em' : '0.05em',
                    color: isRetro ? '#888' : 'text.secondary',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {title}
                </Typography>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.heading : 'inherit',
                    fontSize: isMobile ? '1.25rem' : (isRetro ? '2rem' : '1.75rem'),
                    fontWeight: 700,
                    color: isRetro ? color : 'text.primary',
                    lineHeight: 1,
                    textShadow: isRetro ? `0 0 20px ${color}40` : 'none',
                }}>
                    {value}
                </Typography>
            </Box>
            <Box sx={{
                color: color,
                opacity: 0.8,
                filter: isRetro ? `drop-shadow(0 0 6px ${color}60)` : 'none',
                ml: 1,
                display: isMobile ? 'none' : 'block',
            }}>
                <Icon sx={{ fontSize: 28 }} />
            </Box>
        </Box>
    </Box>
);

/**
 * Get color for an activity event based on its category/action
 */
const getEventColor = (event) => {
    if (!event || !event.action) return CATEGORY_COLORS.default;

    const action = event.action;

    if (action.includes('project')) return CATEGORY_COLORS.project;
    if (action.includes('scenario')) return CATEGORY_COLORS.scenario;
    if (action.includes('optimization')) return CATEGORY_COLORS.optimization;
    if (action.includes('page') || action.includes('view')) return CATEGORY_COLORS.navigation;
    if (action.includes('session')) return CATEGORY_COLORS.session;

    return CATEGORY_COLORS.default;
};

/**
 * ActivityFeedItem component - displays a single activity event
 */
const ActivityFeedItem = ({ event, isRetro }) => {
    const eventColor = getEventColor(event);
    const description = getActionDescription(event);
    const relativeTime = formatRelativeTime(event.timestamp);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                py: 1.5,
                px: 1,
                borderBottom: isRetro
                    ? '1px solid rgba(63, 106, 100, 0.2)'
                    : '1px solid',
                borderColor: isRetro ? undefined : 'divider',
                '&:last-child': {
                    borderBottom: 'none',
                },
                '&:hover': {
                    backgroundColor: isRetro
                        ? 'rgba(95, 212, 196, 0.05)'
                        : 'action.hover',
                },
            }}
        >
            {/* Color dot indicator */}
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: eventColor,
                    boxShadow: isRetro ? `0 0 6px ${eventColor}` : 'none',
                    mt: 0.75,
                    flexShrink: 0,
                }}
            />

            {/* Event content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                        fontSize: '0.8rem',
                        color: isRetro ? '#c8c8c8' : 'text.primary',
                        lineHeight: 1.4,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            color: isRetro ? '#5fd4c4' : 'primary.main',
                        }}
                    >
                        {event.user_name || 'Unknown User'}
                    </Box>
                    {' '}{description}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.5,
                    }}
                >
                    {event.company_name && (
                        <Typography
                            sx={{
                                fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                fontSize: '0.7rem',
                                color: isRetro ? '#666' : 'text.secondary',
                            }}
                        >
                            {event.company_name}
                        </Typography>
                    )}
                    {event.company_name && relativeTime && (
                        <Typography
                            sx={{
                                color: isRetro ? '#444' : 'text.disabled',
                                fontSize: '0.7rem',
                            }}
                        >
                            |
                        </Typography>
                    )}
                    {relativeTime && (
                        <Typography
                            sx={{
                                fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                fontSize: '0.7rem',
                                color: isRetro ? '#666' : 'text.secondary',
                            }}
                        >
                            {relativeTime}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * Section header component
 */
const SectionHeader = ({ title, isRetro }) => (
    <Typography sx={{
        fontFamily: isRetro ? vortxTypography.heading : 'inherit',
        fontSize: isRetro ? '0.9rem' : '1rem',
        fontWeight: 700,
        color: isRetro ? '#b28c50' : 'text.primary',
        textTransform: isRetro ? 'uppercase' : 'none',
        letterSpacing: isRetro ? '0.1em' : 'normal',
        mb: 2,
        pb: 1,
        borderBottom: isRetro ? '1px solid rgba(178, 140, 80, 0.3)' : 'none',
    }}>
        {title}
    </Typography>
);

/**
 * Status chip component for user presence status
 */
const StatusChip = ({ status, isRetro }) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.offline;

    return (
        <Chip
            icon={
                <CircleIcon
                    sx={{
                        fontSize: '8px !important',
                        color: `${color} !important`,
                    }}
                />
            }
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            size="small"
            sx={{
                fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                fontSize: '0.7rem',
                backgroundColor: isRetro ? `${color}15` : `${color}20`,
                color: color,
                border: `1px solid ${color}40`,
                borderRadius: isRetro ? 0 : 2,
                '& .MuiChip-icon': {
                    marginLeft: '6px',
                },
            }}
        />
    );
};

/**
 * ActivityMonitorPage - Real-time user activity monitoring for admins
 */
const ActivityMonitorPage = () => {
    const { userProfile, currentUser } = useAuth();
    const { isRetro } = useThemeMode();
    const { isMobile } = useResponsive();

    const isSuperAdmin = userProfile?.is_super_admin;
    const isCompanyAdmin = userProfile?.is_company_admin;
    const isAdmin = isSuperAdmin || isCompanyAdmin;

    // State
    const [loading, setLoading] = useState(true);
    const [activeUsers, setActiveUsers] = useState([]);  // Real-time presence data
    const [allTeamMembers, setAllTeamMembers] = useState([]);  // All users from API
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState({
        activeUsers: 0,
        eventsLastHour: 0,
        optimizationsToday: 0,
        totalTeamMembers: 0,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('all'); // 'all' or 'online'
    const [scope, setScope] = useState('myTeam'); // 'myTeam' or 'global' (super admin only)

    // Company ID filter based on scope
    // - For company admins: always their company
    // - For super admins: their company when 'myTeam', null (all) when 'global'
    const companyId = isSuperAdmin
        ? (scope === 'myTeam' ? userProfile?.company_id : null)
        : userProfile?.company_id;

    // Fetch all team members from API
    const fetchTeamMembers = useCallback(async () => {
        if (!currentUser) return;

        try {
            const token = await currentUser.getIdToken();

            let allUsers = [];

            if (isSuperAdmin && scope === 'global') {
                // For global scope, fetch all companies first, then users for each
                const companiesResponse = await fetch(`${API_BASE_URL}/api/v1/admin/companies`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!companiesResponse.ok) {
                    console.error('Failed to fetch companies:', companiesResponse.status);
                    return;
                }

                const companiesData = await companiesResponse.json();
                const companies = companiesData.companies || [];

                // Fetch users for each company
                for (const company of companies) {
                    try {
                        const usersResponse = await fetch(`${API_BASE_URL}/api/v1/admin/companies/${company.id}/users`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        if (usersResponse.ok) {
                            const usersData = await usersResponse.json();
                            const companyUsers = (usersData.users || []).map(user => ({
                                ...user,
                                company_id: company.id,
                                company_name: company.name,
                            }));
                            allUsers = [...allUsers, ...companyUsers];
                        }
                    } catch (err) {
                        console.error(`Failed to fetch users for company ${company.id}:`, err);
                    }
                }
            } else {
                // For my team scope, fetch just the current company's users
                const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    console.error('Failed to fetch team members:', response.status);
                    return;
                }

                const data = await response.json();
                allUsers = data.users || [];
            }

            // Enrich users with last activity if needed
            const enrichedUsers = await Promise.all(
                allUsers.map(async (user) => {
                    const lastActivity = await getUserLastActivity(user.uid);
                    return {
                        ...user,
                        id: user.uid,
                        user_id: user.uid,
                        user_name: user.display_name || user.email?.split('@')[0] || 'Unknown',
                        user_email: user.email,
                        last_activity: lastActivity,
                        status: 'offline', // Default, will be merged with presence
                    };
                })
            );

            setAllTeamMembers(enrichedUsers);
            setStats(prev => ({ ...prev, totalTeamMembers: enrichedUsers.length }));
        } catch (error) {
            console.error('Failed to fetch team members:', error);
        }
    }, [currentUser, isSuperAdmin, scope]);

    // Subscribe to presence and events
    useEffect(() => {
        if (!isAdmin) {
            setLoading(false);
            return;
        }

        let unsubscribePresence = () => {};
        let unsubscribeEvents = () => {};
        let statsInterval = null;

        const initializeSubscriptions = async () => {
            // Fetch team members first
            await fetchTeamMembers();

            // Fetch initial stats
            try {
                const initialStats = await getActivityStats({ companyId });
                setStats(prev => ({ ...prev, ...initialStats }));
            } catch (error) {
                console.error('Failed to fetch initial activity stats:', error);
            }

            // Subscribe to presence updates
            unsubscribePresence = subscribeToPresence({ companyId }, (presenceData) => {
                setActiveUsers(presenceData);
                setLoading(false);
            });

            // Subscribe to activity events
            unsubscribeEvents = subscribeToEvents({ companyId, maxEvents: 100 }, (eventData) => {
                setEvents(eventData);
            });

            // Set up interval to refresh stats every 30 seconds
            statsInterval = setInterval(async () => {
                try {
                    const refreshedStats = await getActivityStats({ companyId });
                    setStats(prev => ({ ...prev, ...refreshedStats }));
                } catch (error) {
                    console.error('Failed to refresh activity stats:', error);
                }
            }, 30000);

            setLoading(false);
        };

        initializeSubscriptions();

        // Cleanup function
        return () => {
            unsubscribePresence();
            unsubscribeEvents();
            if (statsInterval) {
                clearInterval(statsInterval);
            }
        };
    }, [isAdmin, companyId, fetchTeamMembers, scope]);

    // Merge team members with presence data
    const mergedUsers = useMemo(() => {
        // Create a map of presence data by user ID
        // Use both 'id' and 'user_id' as potential keys for matching
        const presenceMap = new Map();
        activeUsers.forEach(presence => {
            const id = presence.id || presence.user_id;
            if (id) presenceMap.set(id, presence);
        });

        // Track which user IDs we've already added to prevent duplicates
        const addedUserIds = new Set();

        // Merge team members with presence
        const merged = [];
        allTeamMembers.forEach(member => {
            const memberId = member.id || member.user_id || member.uid;
            if (!memberId || addedUserIds.has(memberId)) return; // Skip duplicates

            addedUserIds.add(memberId);
            const presence = presenceMap.get(memberId);

            if (presence) {
                merged.push({
                    ...member,
                    ...presence,
                    id: memberId, // Ensure consistent ID
                    user_name: presence.user_name || member.user_name,
                    user_email: presence.user_email || member.user_email,
                    last_activity: presence.last_activity || member.last_activity,
                });
            } else {
                merged.push({ ...member, id: memberId });
            }
        });

        // Add any online users not in team members list (for super admin view)
        if (isSuperAdmin) {
            activeUsers.forEach(presence => {
                const presenceId = presence.id || presence.user_id;
                if (presenceId && !addedUserIds.has(presenceId)) {
                    addedUserIds.add(presenceId);
                    merged.push({ ...presence, id: presenceId });
                }
            });
        }

        // Sort: online first, then by last activity
        merged.sort((a, b) => {
            const statusOrder = { active: 0, online: 1, idle: 2, offline: 3 };
            const statusDiff = (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
            if (statusDiff !== 0) return statusDiff;

            const timeA = a.last_activity ? a.last_activity.getTime() : 0;
            const timeB = b.last_activity ? b.last_activity.getTime() : 0;
            return timeB - timeA;
        });

        return merged;
    }, [allTeamMembers, activeUsers, isSuperAdmin]);

    // Calculate display stats from merged users (ensures consistency between stats and displayed list)
    const displayStats = useMemo(() => {
        const totalTeamMembers = mergedUsers.length;
        // Count users that are online (matching the "online" view mode filter)
        const onlineUsers = mergedUsers.filter(user =>
            user.status === 'online' || user.status === 'active' || user.status === 'idle'
        ).length;

        return {
            totalTeamMembers,
            activeUsers: onlineUsers,
        };
    }, [mergedUsers]);

    // Filter users by search term and view mode
    const filteredUsers = useMemo(() => {
        let users = mergedUsers;

        // Filter by view mode
        if (viewMode === 'online') {
            users = users.filter(user =>
                user.status === 'online' || user.status === 'active' || user.status === 'idle'
            );
        }

        // Filter by search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            users = users.filter(user =>
                (user.user_name && user.user_name.toLowerCase().includes(term)) ||
                (user.user_email && user.user_email.toLowerCase().includes(term)) ||
                (user.company_name && user.company_name.toLowerCase().includes(term)) ||
                (user.current_page && user.current_page.toLowerCase().includes(term))
            );
        }

        return users;
    }, [mergedUsers, searchTerm, viewMode]);

    // Filter events - exclude page_view events and limit to 50
    const filteredEvents = useMemo(() => {
        return events
            .filter(event => event.action !== 'page_view' && event.action !== 'viewed_page')
            .slice(0, 50);
    }, [events]);

    // Permission denied state
    if (!isAdmin) {
        return (
            <AdminLayout title="Activity Monitor">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                            fontSize: '1rem',
                            color: isRetro ? '#e74c3c' : 'error.main',
                            mb: 2,
                        }}
                    >
                        Permission Denied
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                            fontSize: '0.85rem',
                            color: isRetro ? '#666' : 'text.secondary',
                        }}
                    >
                        You need admin privileges to view this page.
                    </Typography>
                </Box>
            </AdminLayout>
        );
    }

    // Loading state
    if (loading) {
        return (
            <AdminLayout title="Activity Monitor">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: isRetro ? '#5fd4c4' : 'primary.main' }} />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Activity Monitor">
            {/* Stats Row */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            title={isMobile ? 'Team' : 'Team Members'}
                            value={displayStats.totalTeamMembers}
                            icon={TeamIcon}
                            color="#9b59b6"
                            isRetro={isRetro}
                            isMobile={isMobile}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            title={isMobile ? 'Online' : 'Users Online'}
                            value={displayStats.activeUsers}
                            icon={PersonIcon}
                            color="#00ff41"
                            isRetro={isRetro}
                            isMobile={isMobile}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            title={isMobile ? 'Events' : 'Events (1hr)'}
                            value={stats.eventsLastHour}
                            icon={TrendingUpIcon}
                            color="#5fd4c4"
                            isRetro={isRetro}
                            isMobile={isMobile}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            title={isMobile ? 'Optimizations' : 'Optimizations Today'}
                            value={stats.optimizationsToday}
                            icon={ScheduleIcon}
                            color="#b28c50"
                            isRetro={isRetro}
                            isMobile={isMobile}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Scope Toggle - Super Admin Only */}
            {isSuperAdmin && (
                <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup
                        value={scope}
                        exclusive
                        fullWidth={isMobile}
                        orientation={isMobile ? 'vertical' : 'horizontal'}
                        onChange={(e, newScope) => {
                            if (newScope !== null) {
                                setScope(newScope);
                                setLoading(true);
                            }
                        }}
                        size="small"
                        sx={{
                            width: isMobile ? '100%' : 'auto',
                            '& .MuiToggleButton-root': {
                                fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: isMobile ? 1.5 : 2,
                                py: 0.75,
                                borderRadius: isRetro ? 0 : 1,
                                borderColor: isRetro ? 'rgba(63, 106, 100, 0.4)' : 'divider',
                                color: isRetro ? '#888' : 'text.secondary',
                                '&.Mui-selected': {
                                    backgroundColor: isRetro ? 'rgba(95, 212, 196, 0.15)' : 'primary.light',
                                    color: isRetro ? '#5fd4c4' : 'primary.main',
                                    borderColor: isRetro ? '#5fd4c4' : 'primary.main',
                                    '&:hover': {
                                        backgroundColor: isRetro ? 'rgba(95, 212, 196, 0.25)' : 'primary.light',
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: isRetro ? 'rgba(95, 212, 196, 0.08)' : 'action.hover',
                                },
                            },
                        }}
                    >
                        <ToggleButton value="myTeam">
                            <CompanyIcon sx={{ fontSize: 16, mr: 1 }} />
                            Vortx
                        </ToggleButton>
                        <ToggleButton value="global">
                            <GlobalIcon sx={{ fontSize: 16, mr: 1 }} />
                            {isMobile ? 'Global' : 'All Companies'}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Two-column layout: Active Users + Live Activity Feed */}
            <Grid container spacing={3}>
                {/* Left Column: Team Members Table */}
                <Grid item xs={12} md={7}>
                    <Box>
                        <SectionHeader title={scope === 'global' ? 'Global Activity' : 'Team Activity'} isRetro={isRetro} />

                        {/* View Mode Tabs */}
                        <Box sx={{ mb: 2 }}>
                            <Tabs
                                value={viewMode}
                                onChange={(e, newValue) => setViewMode(newValue)}
                                variant={isMobile ? 'fullWidth' : 'standard'}
                                sx={{
                                    minHeight: 36,
                                    '& .MuiTab-root': {
                                        minHeight: 36,
                                        minWidth: isMobile ? 'auto' : 120,
                                        fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                        fontSize: isMobile ? '0.65rem' : '0.75rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: isRetro ? '#888' : 'text.secondary',
                                        px: isMobile ? 1 : 2,
                                        '&.Mui-selected': {
                                            color: isRetro ? '#5fd4c4' : 'primary.main',
                                        },
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: isRetro ? '#5fd4c4' : 'primary.main',
                                    },
                                }}
                            >
                                <Tab
                                    value="all"
                                    icon={<TeamIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                    iconPosition="start"
                                    label={isMobile ? `All (${displayStats.totalTeamMembers})` : `All Members (${displayStats.totalTeamMembers})`}
                                />
                                <Tab
                                    value="online"
                                    icon={<PersonIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                    iconPosition="start"
                                    label={`Online (${displayStats.activeUsers})`}
                                />
                            </Tabs>
                        </Box>

                        {/* Search field */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            sx={{
                                                color: isRetro ? '#666' : 'text.secondary',
                                                fontSize: 20,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {/* Users table */}
                        <TableContainer
                            sx={{
                                backgroundColor: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
                                border: isRetro ? '1px solid rgba(63, 106, 100, 0.4)' : '1px solid',
                                borderColor: isRetro ? undefined : 'divider',
                                borderRadius: isRetro ? 0 : 2,
                                overflowX: 'auto',
                                // Mobile-friendly scrollbar
                                '&::-webkit-scrollbar': {
                                    height: 6,
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: isRetro ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: isRetro ? 'rgba(95, 212, 196, 0.3)' : 'rgba(0,0,0,0.2)',
                                    borderRadius: isRetro ? 0 : 3,
                                },
                            }}
                        >
                            <Table size="small" sx={{ minWidth: isMobile ? 400 : 'auto' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>User</TableCell>
                                        {isSuperAdmin && scope === 'global' && <TableCell sx={{ whiteSpace: 'nowrap' }}>Company</TableCell>}
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                                        {!isMobile && <TableCell sx={{ whiteSpace: 'nowrap' }}>Current Page</TableCell>}
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{isMobile ? 'Last Active' : 'Last Activity'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={
                                                    (isSuperAdmin && scope === 'global' ? 1 : 0) +
                                                    (isMobile ? 3 : 4)
                                                }
                                                sx={{ textAlign: 'center', py: 4 }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: isRetro
                                                            ? "'JetBrains Mono', monospace"
                                                            : 'inherit',
                                                        fontSize: '0.85rem',
                                                        color: isRetro ? '#666' : 'text.secondary',
                                                    }}
                                                >
                                                    {searchTerm
                                                        ? 'No users match your search'
                                                        : viewMode === 'online'
                                                            ? 'No users currently online'
                                                            : 'No team members found'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        <Avatar
                                                            src={user.avatar_url}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                backgroundColor: isRetro
                                                                    ? 'rgba(95, 212, 196, 0.2)'
                                                                    : 'primary.light',
                                                                border: isRetro
                                                                    ? '1px solid rgba(95, 212, 196, 0.4)'
                                                                    : 'none',
                                                            }}
                                                        >
                                                            {user.user_name
                                                                ? user.user_name.charAt(0).toUpperCase()
                                                                : '?'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: isRetro
                                                                        ? "'JetBrains Mono', monospace"
                                                                        : 'inherit',
                                                                    fontSize: '0.85rem',
                                                                    fontWeight: 600,
                                                                    color: isRetro
                                                                        ? '#c8c8c8'
                                                                        : 'text.primary',
                                                                }}
                                                            >
                                                                {user.user_name || 'Unknown'}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: isRetro
                                                                        ? "'JetBrains Mono', monospace"
                                                                        : 'inherit',
                                                                    fontSize: '0.7rem',
                                                                    color: isRetro
                                                                        ? '#666'
                                                                        : 'text.secondary',
                                                                }}
                                                            >
                                                                {user.user_email || ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                {isSuperAdmin && scope === 'global' && (
                                                    <TableCell>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: isRetro
                                                                    ? "'JetBrains Mono', monospace"
                                                                    : 'inherit',
                                                                fontSize: '0.8rem',
                                                                color: isRetro
                                                                    ? '#888'
                                                                    : 'text.secondary',
                                                            }}
                                                        >
                                                            {user.company_name || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <StatusChip
                                                        status={user.status || 'offline'}
                                                        isRetro={isRetro}
                                                    />
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell sx={{ maxWidth: 200 }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: isRetro
                                                                    ? "'JetBrains Mono', monospace"
                                                                    : 'inherit',
                                                                fontSize: '0.8rem',
                                                                color: isRetro
                                                                    ? '#888'
                                                                    : 'text.secondary',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                maxWidth: 200,
                                                                display: 'block',
                                                            }}
                                                            title={user.current_page || ''}
                                                        >
                                                            {user.current_page || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: isRetro
                                                                ? "'JetBrains Mono', monospace"
                                                                : 'inherit',
                                                            fontSize: '0.8rem',
                                                            color: isRetro
                                                                ? '#666'
                                                                : 'text.secondary',
                                                        }}
                                                    >
                                                        {formatRelativeTime(user.last_activity)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>

                {/* Right Column: Live Activity Feed */}
                <Grid item xs={12} md={5}>
                    <Box>
                        <SectionHeader title="Live Activity Feed" isRetro={isRetro} />

                        <Box
                            sx={{
                                backgroundColor: isRetro
                                    ? 'rgba(0, 0, 0, 0.4)'
                                    : 'background.paper',
                                border: isRetro
                                    ? '1px solid rgba(63, 106, 100, 0.4)'
                                    : '1px solid',
                                borderColor: isRetro ? undefined : 'divider',
                                borderRadius: isRetro ? 0 : 2,
                                maxHeight: isMobile ? 300 : 500,
                                overflowY: 'auto',
                                // Custom scrollbar for retro mode
                                ...(isRetro && {
                                    '&::-webkit-scrollbar': {
                                        width: 6,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'rgba(0, 0, 0, 0.3)',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(95, 212, 196, 0.3)',
                                        borderRadius: 0,
                                        '&:hover': {
                                            background: 'rgba(95, 212, 196, 0.5)',
                                        },
                                    },
                                }),
                            }}
                        >
                            {filteredEvents.length === 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 4,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: isRetro
                                                ? "'JetBrains Mono', monospace"
                                                : 'inherit',
                                            fontSize: '0.85rem',
                                            color: isRetro ? '#666' : 'text.secondary',
                                        }}
                                    >
                                        No recent activity
                                    </Typography>
                                </Box>
                            ) : (
                                filteredEvents.map((event) => (
                                    <ActivityFeedItem
                                        key={event.id}
                                        event={event}
                                        isRetro={isRetro}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </AdminLayout>
    );
};

export default ActivityMonitorPage;
